import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router";
import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import {
  Alert,
  Badge,
  Button,
  Card,
  Col,
  Flex,
  Form,
  Grid,
  Input,
  Modal,
  Pagination,
  Popconfirm,
  Popover,
  Row,
  Select,
  Space,
  Switch,
  Table,
  Tag,
  Typography,
  message,
} from "antd";
const { Text } = Typography;
import { Icon } from "@iconify/react";
import { format } from "date-fns";
import useTitleStore from "../../../store/titleStore";
import pallete from "../../../utils/pallete";
import DetailSection from "../components/DetailSection";
import useDebounce from "../../../hooks/useDebounce";
import { useTableHeight } from "../../../hooks/useTableHeight";
import UpdatedByCell from "../../../components/UpdatedByCell";
import { ministryVocationalInstitutionService } from "../../../services/ministry-vocational-institution.service";
import { governingMinistryService } from "../../../services/governingMinistry.service";
import { fetchProvinces } from "../../../services/province.service";

export default function MinistryVocationalInstitutionIndex() {
  const breakpoint = Grid.useBreakpoint();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { setTitle } = useTitleStore();
  const tableHeight = useTableHeight(430);
  const [messageApi, contextHolder] = message.useMessage();

  // Filter state for Ministry Vocational Institution
  const [mviKeyword, setMviKeyword] = useState("");
  const debouncedMviKeyword = useDebounce(mviKeyword, 500);
  const [mviStatus, setMviStatus] = useState(undefined);
  const [mviMinistry, setMviMinistry] = useState(undefined);
  const [mviProvince, setMviProvince] = useState(undefined);
  const [mviParams, setMviParams] = useState({
    limit: 10,
    page: 1,
    query: undefined,
    isActive: undefined,
    governingMinistryId: undefined,
    province: undefined,
  });

  const [exportLoading, setExportLoading] = useState(false);

  // Modal states
  const [addModalVisible, setAddModalVisible] = useState(false);
  const [addForm] = Form.useForm();

  const [detailModalVisible, setDetailModalVisible] = useState(false);
  const [selectedDetail, setSelectedDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);

  // Set page title
  useEffect(() => {
    setTitle("Lembaga Vokasi Kementerian");
  }, [setTitle]);

  // Fetch provinces for filter
  const { data: provinces = [] } = useQuery({
    queryKey: ["provinces"],
    queryFn: fetchProvinces,
    staleTime: 24 * 60 * 60 * 1000,
  });

  const provinceOptions = useMemo(
    () =>
      provinces.map((prov) => ({
        label: prov.nama,
        value: prov.nama,
      })),
    [provinces],
  );

  // Fetch Governing Ministry master data (without pagination)
  const { data: governingMinistryData, isLoading: isMinistryLoading } =
    useQuery({
      queryKey: ["governing-ministry-all"],
      queryFn: () => governingMinistryService.fetchAllWithoutPaginate(),
    });

  const ministryList = useMemo(() => {
    if (!governingMinistryData) return [];
    if (Array.isArray(governingMinistryData)) return governingMinistryData;
    if (Array.isArray(governingMinistryData?.data))
      return governingMinistryData.data;
    return [];
  }, [governingMinistryData]);

  const ministryOptions = useMemo(
    () =>
      ministryList.map((m) => ({
        label: m.name,
        value: m.uuid || m.id,
      })),
    [ministryList],
  );

  // Sync Ministry Vocational Institution params
  useEffect(() => {
    setMviParams((prev) => ({
      ...prev,
      page: 1,
      query: debouncedMviKeyword || undefined,
      isActive:
        mviStatus !== undefined && mviStatus !== "" ? mviStatus : undefined,
      governingMinistryId: mviMinistry || undefined,
      province: mviProvince || undefined,
    }));
  }, [debouncedMviKeyword, mviStatus, mviMinistry, mviProvince]);

  // Fetch Ministry Vocational Institution list
  const {
    data: ministryVocationalData,
    isLoading: isMviLoading,
    isRefetching: isMviRefetching,
  } = useQuery({
    queryKey: ["ministry-vocational-institution", mviParams],
    queryFn: () => ministryVocationalInstitutionService.fetchAll(mviParams),
    placeholderData: keepPreviousData,
  });

  // Fetch Eligible Institutions list (for selection dropdown when adding)
  const { data: eligibleData, isLoading: isEligibleLoading } = useQuery({
    queryKey: ["ministry-vocational-institution-eligible"],
    queryFn: () =>
      ministryVocationalInstitutionService.fetchEligibleInstitutions({
        all: true,
      }),
  });

  // Parse lists
  const mviList = useMemo(() => {
    if (!ministryVocationalData) return [];
    if (Array.isArray(ministryVocationalData)) return ministryVocationalData;
    if (Array.isArray(ministryVocationalData?.data))
      return ministryVocationalData.data;
    return [];
  }, [ministryVocationalData]);

  const eligibleList = useMemo(() => {
    if (!eligibleData) return [];
    if (Array.isArray(eligibleData)) return eligibleData;
    if (Array.isArray(eligibleData?.data)) return eligibleData.data;
    return [];
  }, [eligibleData]);

  // Map of institution IDs that are already registered as Ministry Vocational Institution
  const registeredInstitutionIds = useMemo(() => {
    const ids = new Set();
    mviList.forEach((item) => {
      if (item.vocationalInstitutionId)
        ids.add(String(item.vocationalInstitutionId));
      if (item.vocationalInstitution?.id)
        ids.add(String(item.vocationalInstitution.id));
      if (item.vocationalInstitution?.uuid)
        ids.add(String(item.vocationalInstitution.uuid));
      if (item.institutionId) ids.add(String(item.institutionId));
      if (item.id) ids.add(String(item.id));
      if (item.uuid) ids.add(String(item.uuid));
    });
    return ids;
  }, [mviList]);

  // Eligible options for dropdown (filter out already registered ones)
  const eligibleSelectOptions = useMemo(() => {
    return eligibleList
      .filter((inst) => {
        if (inst.isMinistryInstitution) return false;
        const idToCheck = String(inst.id || inst.uuid || inst.institutionId);
        return !registeredInstitutionIds.has(idToCheck);
      })
      .map((inst) => {
        const id = inst.uuid || inst.id;
        return {
          label: `${inst.name}`,
          value: id,
        };
      });
  }, [eligibleList, registeredInstitutionIds]);

  // Mutations
  const addMutation = useMutation({
    mutationFn: (payload) =>
      ministryVocationalInstitutionService.store(payload),
    onSuccess: (res) => {
      messageApi.success(
        res?.message ||
          "Lembaga berhasil ditambahkan sebagai Lembaga Vokasi Kementerian",
      );
      queryClient.invalidateQueries({
        queryKey: ["ministry-vocational-institution"],
      });
      queryClient.invalidateQueries({
        queryKey: ["ministry-vocational-institution-eligible"],
      });
      setAddModalVisible(false);
      addForm.resetFields();
    },
    onError: (err) => {
      const errMsg =
        err?.response?.data?.message ||
        "Gagal menambahkan Lembaga ke Lembaga Vokasi Kementerian";
      messageApi.error(errMsg);
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }) =>
      ministryVocationalInstitutionService.update(id, data),
    onSuccess: () => {
      messageApi.success("Data Lembaga Vokasi Kementerian berhasil diperbarui");
      queryClient.invalidateQueries({
        queryKey: ["ministry-vocational-institution"],
      });
      queryClient.invalidateQueries({
        queryKey: ["ministry-vocational-institution-eligible"],
      });
    },
    onError: (err) => {
      const errMsg =
        err?.response?.data?.message ||
        "Gagal memperbarui data Lembaga Vokasi Kementerian";
      messageApi.error(errMsg);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id) => ministryVocationalInstitutionService.delete(id),
    onSuccess: () => {
      messageApi.success(
        "Lembaga berhasil dihapus dari Lembaga Vokasi Kementerian",
      );
      queryClient.invalidateQueries({
        queryKey: ["ministry-vocational-institution"],
      });
      queryClient.invalidateQueries({
        queryKey: ["ministry-vocational-institution-eligible"],
      });
      setDetailModalVisible(false);
    },
    onError: (err) => {
      const errMsg =
        err?.response?.data?.message ||
        "Gagal menghapus data Lembaga Vokasi Kementerian";
      messageApi.error(errMsg);
    },
  });

  // Handle Export Excel
  const handleExportExcel = async () => {
    try {
      setExportLoading(true);
      const response = await ministryVocationalInstitutionService.exportExcel({
        query: mviParams.query,
        isActive: mviParams.isActive,
        governingMinistryId: mviParams.governingMinistryId,
        province: mviParams.province,
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      const fileName =
        response.headers["content-disposition"]
          ?.split("filename=")?.[1]
          ?.replace(/"/g, "") || "lembaga-vokasi-kementerian.xlsx";
      link.setAttribute("download", fileName);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      messageApi.success("File berhasil diunduh");
    } catch {
      messageApi.error("Gagal mengunduh file Excel");
    } finally {
      setExportLoading(false);
    }
  };

  const handleOpenAddModal = () => {
    addForm.resetFields();
    setAddModalVisible(true);
  };

  const handleConfirmAdd = async () => {
    try {
      const values = await addForm.validateFields();
      addMutation.mutate({
        governingMinistryId: values.governingMinistryId,
        vocationalInstitutionIds: values.vocationalInstitutionIds,
        isActive: true,
      });
    } catch (err) {
      console.error(err);
    }
  };

  // Open detail modal
  const handleOpenDetail = async (record) => {
    const id = record.uuid || record.id;
    setDetailModalVisible(true);
    setSelectedDetail(record);
    if (id) {
      try {
        setDetailLoading(true);
        const detailRes =
          await ministryVocationalInstitutionService.findById(id);
        if (detailRes?.data) {
          setSelectedDetail(detailRes.data);
        }
      } catch (err) {
        console.error("Failed to load detail", err);
      } finally {
        setDetailLoading(false);
      }
    }
  };

  // Helper getters
  const inst = selectedDetail?.vocationalInstitution;
  const getInstName = (r) =>
    r.vocationalInstitution?.name || r.institutionName || r.name || "-";
  const getInstCode = (r) =>
    r.vocationalInstitution?.institutionId ||
    r.institutionId ||
    r.vocationalInstitutionId ||
    "-";
  const getInstType = (r) =>
    r.vocationalInstitution?.type?.name ||
    r.vocationalInstitution?.type ||
    r.type ||
    "-";
  const getInstForm = (r) =>
    r.vocationalInstitution?.institutionForm || r.institutionForm || "-";
  const getMinistryName = (r) =>
    r.governingMinistry?.name || r.governingMinistryName || "-";

  // Ministry Vocational Institution Columns
  const mviColumns = [
    {
      title: "No",
      align: "center",
      width: 55,
      fixed: breakpoint.md && "left",
      render: (_, __, index) => {
        const page =
          ministryVocationalData?.pagination?.page || mviParams.page || 1;
        const limit = mviParams.limit || 10;
        return (page - 1) * limit + index + 1;
      },
    },
    {
      title: "ID & Nama Lembaga",
      dataIndex: "name",
      width: 260,
      fixed: breakpoint.md && "left",
      render: (_, record) => (
        <Space direction="vertical" size={2}>
          <Typography.Text strong style={{ fontSize: 14 }}>
            {getInstName(record)}
          </Typography.Text>
          <Typography.Text
            type="secondary"
            style={{ fontSize: 12, fontStyle: "italic" }}
          >
            {getInstCode(record)}
          </Typography.Text>
        </Space>
      ),
    },
    {
      title: "Kementerian Pengampu",
      width: 160,
      render: (_, record) => (
        <Text strong>{record.governingMinistry?.name}</Text>
      ),
    },
    {
      title: "Jenis & Status Lembaga",
      width: 120,
      render: (_, record) => (
        <Space direction="vertical" size={2}>
          <Typography.Text strong>{getInstType(record)}</Typography.Text>
          <Typography.Text type="secondary" style={{ fontSize: 12 }}>
            {getInstForm(record)}
          </Typography.Text>
        </Space>
      ),
    },
    {
      title: "Provinsi & Kota Lembaga",
      dataIndex: "province",
      width: 120,
      render: (_, record) => (
        <>
          <Typography.Text strong>
            {record.vocationalInstitution?.province ?? "-"}
          </Typography.Text>
          <br />
          <Typography.Text type="secondary" style={{ fontSize: 13 }}>
            {record.vocationalInstitution?.district ?? "-"}
          </Typography.Text>
        </>
      ),
    },
    {
      title: "Status Lembaga",
      dataIndex: "isActive",
      width: 120,
      render: (isActive) => {
        const active = Boolean(isActive);
        return (
          <Tag color={active ? "success" : "default"}>
            {active ? "Aktif" : "Nonaktif"}
          </Tag>
        );
      },
    },
    {
      title: "Update By",
      width: 150,
      render: (record) => (
        <UpdatedByCell
          value={{
            ...record,
            updatedBy:
              record.updatedBy?.fullName ||
              record.updatedBy?.name ||
              record.updatedBy?.username ||
              (typeof record.updatedBy === "string"
                ? record.updatedBy
                : undefined),
          }}
        />
      ),
    },
    {
      className: "last-cell-p",
      title: "Aksi",
      align: "right",
      fixed: "right",
      width: 80,
      render: (record) => (
        <Button
          icon={<Icon icon="solar:eye-bold-duotone" width={18} height={18} />}
          color="default"
          variant="filled"
          onClick={() => handleOpenDetail(record)}
        />
      ),
    },
  ];

  const activeFilterCount = [mviMinistry, mviStatus, mviProvince].filter(
    Boolean,
  ).length;

  const filterContent = (
    <div style={{ width: breakpoint.sm ? 460 : 280, padding: "4px 0" }}>
      <Flex justify="space-between" align="center" style={{ marginBottom: 12 }}>
        <Typography.Text strong style={{ fontSize: 14 }}>
          Filter Lembaga Vokasi Kementerian
        </Typography.Text>
        {activeFilterCount > 0 && (
          <Button
            type="link"
            size="small"
            danger
            style={{ padding: 0, height: "auto" }}
            onClick={() => {
              setMviMinistry(undefined);
              setMviStatus(undefined);
              setMviProvince(undefined);
            }}
          >
            Reset
          </Button>
        )}
      </Flex>

      <Row gutter={[12, 12]}>
        <Col xs={24} sm={12}>
          <Typography.Text type="secondary" style={{ fontSize: 12 }}>
            Kementerian Pengampu
          </Typography.Text>
          <Select
            allowClear
            showSearch
            optionFilterProp="label"
            loading={isMinistryLoading}
            placeholder="Semua Kementerian Pengampu"
            style={{ width: "100%", marginTop: 4 }}
            value={mviMinistry}
            onChange={(val) => setMviMinistry(val)}
            options={ministryOptions}
          />
        </Col>
        <Col xs={24} sm={12}>
          <Typography.Text type="secondary" style={{ fontSize: 12 }}>
            Status Aktif
          </Typography.Text>
          <Select
            allowClear
            placeholder="Semua Status"
            style={{ width: "100%", marginTop: 4 }}
            value={mviStatus}
            onChange={(val) => setMviStatus(val)}
            options={[
              { label: "Semua Status", value: "" },
              { label: "Aktif", value: "true" },
              { label: "Nonaktif", value: "false" },
            ]}
          />
        </Col>
        <Col xs={24} sm={12}>
          <Typography.Text type="secondary" style={{ fontSize: 12 }}>
            Provinsi
          </Typography.Text>
          <Select
            allowClear
            showSearch
            optionFilterProp="label"
            placeholder="Semua Provinsi"
            style={{ width: "100%", marginTop: 4 }}
            value={mviProvince}
            onChange={(val) => setMviProvince(val)}
            options={provinceOptions}
          />
        </Col>
      </Row>
    </div>
  );

  return (
    <>
      {contextHolder}
      <Card
        styles={{
          body: {
            paddingInline: 24,
            paddingTop: 24,
            paddingBottom: 24,
          },
        }}
      >
        {/* Filters Bar */}
        <Flex
          justify="space-between"
          align="center"
          gap={12}
          wrap="wrap"
          style={{ marginBottom: 20 }}
        >
          <Input
            style={{ width: breakpoint.md ? 260 : "100%", minWidth: 200 }}
            prefix={<Icon icon="hugeicons:search-01" width={16} height={16} />}
            placeholder="Cari nama lembaga / ID..."
            allowClear
            value={mviKeyword}
            onChange={(e) => setMviKeyword(e.target.value)}
          />

          <Flex gap={10} wrap="wrap">
            <Popover
              content={filterContent}
              trigger="click"
              placement="bottomRight"
            >
              <Badge count={activeFilterCount} size="small" offset={[-2, 2]}>
                <Button
                  icon={
                    <Icon
                      icon="solar:filter-bold-duotone"
                      width={18}
                      height={18}
                    />
                  }
                  type={activeFilterCount > 0 ? "primary" : "default"}
                  ghost={activeFilterCount > 0}
                >
                  Filter
                </Button>
              </Badge>
            </Popover>
            <Button
              icon={
                <Icon icon="solar:export-bold-duotone" width={18} height={18} />
              }
              loading={exportLoading}
              onClick={handleExportExcel}
            >
              Export Excel
            </Button>
            <Button
              type="primary"
              icon={
                <Icon icon="solar:add-circle-bold" width={18} height={18} />
              }
              onClick={handleOpenAddModal}
            >
              {breakpoint.md ? "Tambah Lembaga Vokasi Kementerian" : "Tambah"}
            </Button>
          </Flex>
        </Flex>

        {/* Table */}
        <Table
          columns={mviColumns}
          size="small"
          loading={isMviLoading || isMviRefetching}
          rowKey={(r) => r.uuid || r.id || String(Math.random())}
          dataSource={mviList}
          pagination={false}
          scroll={{ y: tableHeight, x: 1200 }}
        />

        {/* Pagination */}
        <Pagination
          style={{ marginTop: 16 }}
          total={ministryVocationalData?.pagination?.total || mviList.length}
          pageSize={mviParams.limit}
          current={ministryVocationalData?.pagination?.page || mviParams.page}
          onChange={(page, limit) =>
            setMviParams((prev) => ({ ...prev, page, limit }))
          }
          showSizeChanger={{ showSearch: false }}
          pageSizeOptions={["10", "20", "50"]}
          align="end"
        />
      </Card>

      {/* Modal: Tambah Lembaga Vokasi Kementerian */}
      <Modal
        title={
          <Flex align="center" gap={8}>
            <Icon
              icon="solar:buildings-2-bold-duotone"
              width={22}
              height={22}
              color={pallete.primary?.[500]}
            />
            <span>Tambahkan Lembaga Vokasi Kementerian</span>
          </Flex>
        }
        open={addModalVisible}
        onCancel={() => {
          setAddModalVisible(false);
          addForm.resetFields();
        }}
        onOk={handleConfirmAdd}
        okText="Tambah Lembaga"
        cancelText="Batal"
        confirmLoading={addMutation.isPending}
        destroyOnHidden
      >
        <Form form={addForm} layout="vertical" style={{ marginTop: 16 }}>
          <Form.Item
            name="governingMinistryId"
            label={
              <>
                <Text>Pilih Kementerian Pengampu</Text>
                <Text style={{ color: "#940808" }}>*</Text>
              </>
            }
            rules={[
              { required: true, message: "Kementerian Pengampu harus dipilih" },
            ]}
            required={false}
          >
            <Select
              showSearch
              optionFilterProp="label"
              placeholder="Cari dan Pilih Kementerian Pengampu"
              loading={isMinistryLoading}
              options={ministryOptions}
            />
          </Form.Item>

          <Form.Item
            name="vocationalInstitutionIds"
            label={
              <>
                <Text>Pilih Lembaga Vokasi</Text>
                <Text style={{ color: "#940808" }}>*</Text>
              </>
            }
            extra="Dapat memilih lebih dari satu lembaga vokasi"
            rules={[
              {
                required: true,
                type: "array",
                min: 1,
                message: "Pilih minimal satu Lembaga Vokasi",
              },
            ]}
            required={false}
          >
            <Select
              mode="multiple"
              allowClear
              showSearch
              optionFilterProp="label"
              maxTagCount="responsive"
              placeholder="Cari dan Pilih Lembaga Vokasi"
              loading={isEligibleLoading}
              options={eligibleSelectOptions}
            />
          </Form.Item>
        </Form>

        <Alert
          message={
            <Text style={{ fontSize: 12, fontWeight: 600, marginBottom: 0 }}>
              Catatan
            </Text>
          }
          type="warning"
          description={
            <Text style={{ fontSize: 12 }}>
              Hanya lembaga vokasi selain Universitas dengan status Aktif yang
              dapat ditambahkan sebagai Lembaga Vokasi Kementerian. Pilihan
              Kementerian Pengampu diambil dari data yang telah ditambahkan pada
              menu Master Data.
            </Text>
          }
          style={{ marginBottom: 32, padding: 15 }}
        />
      </Modal>

      {/* Modal: Detail Lembaga Vokasi Kementerian */}
      <Modal
        title="Detail Informasi Lembaga"
        open={detailModalVisible}
        onCancel={() => setDetailModalVisible(false)}
        footer={[
          <Popconfirm
            key="delete"
            title="Hapus Lembaga Vokasi Kementerian"
            description="Apakah Anda yakin ingin menghapus lembaga ini dari Lembaga Vokasi Kementerian?"
            okText="Hapus"
            cancelText="Batal"
            okButtonProps={{ danger: true, loading: deleteMutation.isPending }}
            onConfirm={() => {
              const recordId = selectedDetail.uuid || selectedDetail.id;
              deleteMutation.mutate(recordId);
            }}
          >
            <Button danger loading={deleteMutation.isPending}>
              Hapus
            </Button>
          </Popconfirm>,
          <Button key="close" onClick={() => setDetailModalVisible(false)}>
            Tutup
          </Button>,
          selectedDetail &&
            (selectedDetail.vocationalInstitution?.uuid ||
              selectedDetail.vocationalInstitutionId) && (
              <Button
                key="view-inst"
                type="primary"
                onClick={() => {
                  const targetId =
                    selectedDetail.vocationalInstitution?.uuid ||
                    selectedDetail.vocationalInstitutionId;
                  navigate(`/institutional-verification/detail/${targetId}`);
                }}
              >
                Lihat Detail Lembaga
              </Button>
            ),
        ]}
        loading={detailLoading}
        width={760}
      >
        {selectedDetail && (
          <div style={{ marginTop: 12, marginBottom: 32 }}>
            <DetailSection
              title="Informasi Profil Lembaga"
              items={[
                {
                  label: "Nama Lembaga",
                  span: 2,
                  children: getInstName(selectedDetail),
                },
                {
                  label: "ID Vokasi Lembaga",
                  span: 2,
                  children: getInstCode(selectedDetail),
                },
                {
                  label: "Jenis Lembaga",
                  children: getInstType(selectedDetail),
                },
                {
                  label: "Status Lembaga",
                  children: inst?.institutionStatus || "-",
                },
              ]}
            />
            <DetailSection
              title="Informasi Pimpinan Lembaga"
              items={[
                {
                  label: "Nama Pimpinan",
                  span: 2,
                  children: inst?.leaderName || "-",
                },
                {
                  label: "Jabatan",
                  span: 2,
                  children: inst?.leaderPosition || "-",
                },
              ]}
            />
            <DetailSection
              title="Informasi Alamat Lembaga"
              items={[
                {
                  label: "Alamat Lengkap",
                  span: 2,
                  children: inst?.address || "-",
                },
                { label: "Provinsi", children: inst?.province || "-" },
                { label: "Kota/Kabupaten", children: inst?.district || "-" },
                {
                  label: "Kode Pos",
                  span: 2,
                  children: inst?.postalCode || "-",
                },
              ]}
            />
            <DetailSection
              title="Informasi Kontak Lembaga"
              items={[
                {
                  label: "Alamat Email",
                  span: 2,
                  children: inst?.email || "-",
                },
                {
                  label: "Nomor Telepon",
                  span: 2,
                  children: inst?.phone || "-",
                },
                {
                  label: "Website/Sosial Media",
                  span: 2,
                  children: inst?.website || "-",
                },
                {
                  label: "Link INAPROC",
                  span: 2,
                  children: inst?.inaprocLink || "-",
                },
              ]}
            />
            <DetailSection
              title="Informasi PIC Lembaga"
              items={[
                {
                  label: "Nama Lengkap",
                  span: 2,
                  children: inst?.pic?.fullName || "-",
                },
                { label: "Jabatan", children: inst?.pic?.position || "-" },
                { label: "Nomor Telepon", children: inst?.pic?.phone || "-" },
              ]}
            />
            <DetailSection
              title="Informasi Kementerian Pengampu"
              items={[
                {
                  label: "Kementerian Pengampu",
                  span: 2,
                  children: (
                    <Select
                      size="small"
                      style={{ width: "100%" }}
                      placeholder="Pilih Kementerian Pengampu"
                      value={
                        selectedDetail.governingMinistry?.uuid ||
                        selectedDetail.governingMinistry?.id ||
                        selectedDetail.governingMinistryId
                      }
                      options={ministryOptions}
                      showSearch
                      optionFilterProp="label"
                      loading={updateMutation.isPending}
                      onChange={(newMinistryId) => {
                        const recordId =
                          selectedDetail.uuid || selectedDetail.id;
                        const selectedMinistryObj = ministryList.find(
                          (m) =>
                            m.uuid === newMinistryId || m.id === newMinistryId,
                        );
                        updateMutation.mutate(
                          {
                            id: recordId,
                            data: { governingMinistryId: newMinistryId },
                          },
                          {
                            onSuccess: () => {
                              setSelectedDetail((prev) => ({
                                ...prev,
                                governingMinistry:
                                  selectedMinistryObj || prev.governingMinistry,
                                governingMinistryId: newMinistryId,
                              }));
                              messageApi.success(
                                "Kementerian Pengampu berhasil diperbarui",
                              );
                            },
                          },
                        );
                      }}
                    />
                  ),
                },
              ]}
            />
            <DetailSection
              title="Status Aktivasi Lembaga Vokasi Kementerian"
              items={[
                {
                  label: "Status Lembaga Vokasi Kementerian",
                  span: 2,
                  children: (
                    <Switch
                      size="small"
                      checked={Boolean(selectedDetail.isActive)}
                      loading={updateMutation.isPending}
                      onChange={(checked) => {
                        const recordId =
                          selectedDetail.uuid || selectedDetail.id;
                        updateMutation.mutate(
                          { id: recordId, data: { isActive: checked } },
                          {
                            onSuccess: () => {
                              setSelectedDetail((prev) => ({
                                ...prev,
                                isActive: checked,
                              }));
                            },
                          },
                        );
                      }}
                      checkedChildren="Aktif"
                      unCheckedChildren="Nonaktif"
                    />
                  ),
                },
                {
                  label: "Terakhir Diperbarui",
                  children: selectedDetail.updatedAt
                    ? format(
                        new Date(selectedDetail.updatedAt),
                        "dd/MM/yyyy HH:mm",
                      )
                    : "-",
                },
                {
                  label: "Diperbarui Oleh",
                  children:
                    selectedDetail.updatedBy?.fullName ||
                    (typeof selectedDetail.updatedBy === "string"
                      ? selectedDetail.updatedBy
                      : "-"),
                },
              ]}
            />
          </div>
        )}
      </Modal>
    </>
  );
}
